import { MGMAuthError } from '@mgmresorts/mgm-auth-js-sdk';
import dayjs from 'dayjs';
import { navigate } from 'hookrouter';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KnownError, UNKNOWN_ERROR_MESSAGE } from '../../api/errors';
import { enrollUser, IEnrollmentPayload, signUpVerificationRequest } from '../../api/fetch/enrollment';
import {
  useAppRedirect,
  BindBirthdayInput,
  useBirthdayInput,
  BindCheckbox,
  useCheckbox,
  useDerivedPropertySlugAsSource,
  useFieldValidation,
  BindInput,
  useInput,
  useMobile,
  usePasswordInput,
  BindSelect,
  useSelect,
  useMfaEnabled,
  useProcessSignInWithIdx,
} from '../../hooks';
import { AddressObject, useAddressSearch } from '../../hooks/useAddressSearch';
import { useIdxApiEnabled } from '../../hooks/useIdxApiEnabled';
import { captureEvent, captureException, captureMessage } from '../../monitoring/logrocket';
import { useEvent } from '../../utils/analytics/useEvent';
import { useAuthContext } from '../../utils/auth/AuthProvider';
import { IDENTITY_WIDGET_PATHS, IDW_FLOW_PARAM, MaxLengths } from '../../utils/constants';
import { usePersistentParameters } from '../../utils/persistParameterContext';
import { redirectToSignIn } from '../../utils/redirect';
import { safePhoneNumber } from '../../utils/safePhoneNumber';
import { useUserAccount } from '../../utils/userAccountContext';
import {
  addressValidator,
  birthdayValidator,
  cityValidator,
  emailValidator,
  firstNameValidator,
  isValidAge,
  isValidZip,
  lastNameValidator,
  passwordValidator,
  phoneNumberValidator,
  stateValidator,
  isValidPhoneNumber,
} from '../../utils/validators';

type Context = {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  birthday: string;
  birthDate: string | string[];
  birthMonth: string | string[];
  birthYear: string | string[];
  phoneNumber: string;
  address: string;
  address2: string;
  city: string;
  zip: string;
  customState: string;
  state: string | string[];
  country: string | string[];
  policy: boolean;
  isLoading: boolean;
  isValidPassword: boolean;
  passwordValidations: { [key: string]: boolean };
  invalidFields: string[];
  processSignUp: () => Promise<void>;
  verifySignupCode: (verificationCode: string) => Promise<void>;
  backToSignUp: (e) => void;
  bindEmail: BindInput;
  bindFirstName: BindInput;
  bindLastName: BindInput;
  bindPolicy: BindCheckbox;
  bindPassword: BindInput;
  bindBirthday: BindBirthdayInput;
  bindBirthDate: BindSelect;
  bindBirthMonth: BindSelect;
  bindBirthYear: BindSelect;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  bindPhoneNumber: any;
  bindAddress: BindInput;
  bindAddress2: BindInput;
  bindCity: BindInput;
  bindCustomState: BindInput;
  bindZip: BindInput;
  bindState: BindSelect;
  bindCountry: BindSelect;
  resetEmail: () => void;
  resetFirstName: () => void;
  resetLastName: () => void;
  resetPolicy: () => void;
  resetCanadianOptIn: () => void;
  resetPassword: () => void;
  resetBirthday: () => void;
  resetPhoneNumber: () => void;
  resetAddress: () => void;
  resetAddress2: () => void;
  resetCity: () => void;
  resetZip: () => void;
  resetState: () => void;
  resetCustomState: () => void;
  resetCountry: () => void;
  canProcessSignUp: boolean;
  placePredictions: google.maps.places.AutocompletePrediction[];
  setSelectedPlaceId: (placeId?: string) => void;
  selectedPlaceId?: string;
  setAddressValue: (address: string) => void;
  showExtendedBirthday: boolean;
  enrollmentError: null | string;
  signUpCodeVerificationError: null | string;
  bindCanadianOptIn: BindCheckbox;
  isCanadian: boolean;
  autocompleteSessionToken?: google.maps.places.AutocompleteSessionToken;
  removeInvalidField: (identifier: string) => void;
  setInvalidField: (identifier: string[]) => void;
  showWelcomeMgmRewardsScreen: boolean;
  useManualEntry: boolean;
  onInputChange: (field: string, value: string) => void;
  setUseManualEntry: (Identifier) => void;
  verificationKid: null | string;
  getCachedPlaceDetails: (
    placeId: string,
  ) => Promise<{ place: google.maps.places.PlaceResult; address?: AddressObject }>;
};

export const SignUpFormContext = createContext<Context>({} as Context);

export const SignUpFormContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const [autocompleteSessionToken, setAutocompleteSessionToken] = useState<
    undefined | google.maps.places.AutocompleteSessionToken
  >();
  const refreshGoogleSessionToken = useCallback(() => {
    if (window?.google) {
      setAutocompleteSessionToken(new google.maps.places.AutocompleteSessionToken());
    }
  }, []);
  const trackEvent = useEvent();
  const [enrollmentError, setEnrollmentErrorState] = useState<null | string>(null);
  const [signUpCodeVerificationError, setSignUpCodeVerificationError] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const [showWelcomeMgmRewardsScreen, setShowWelcomeMgmRewardsScreen] = useState(false);
  const [verificationKid, setVerificationKid] = useState<null | string>(null);
  const [showExtendedBirthday, setShowExtendedBirthday] = useState(true);
  const [useManualEntry, setUseManualEntry] = useState(false);
  const { validateField, invalidFields, removeInvalidField, setInvalidField } = useFieldValidation([
    emailValidator,
    phoneNumberValidator,
    firstNameValidator,
    lastNameValidator,
    birthdayValidator,
    passwordValidator,
    addressValidator,
    cityValidator,
    stateValidator,
  ]);
  const { onInputChange, placePredictions, getPlaceDetails } = useAddressSearch(autocompleteSessionToken);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string | undefined>();
  const placeDetailsCache = useRef<{ placeId: string; result: Awaited<ReturnType<typeof getPlaceDetails>> } | null>(
    null,
  );
  const placeDetailsInflight = useRef<{
    placeId: string;
    promise: Promise<Awaited<ReturnType<typeof getPlaceDetails>>>;
  } | null>(null);
  const [isCanadian, setIsCanadian] = useState(false);
  const { email: userEmail, mgmId, setUserMgmId } = useUserAccount();
  const { mfaEnabled } = useMfaEnabled();

  const redirectToApp = useAppRedirect();

  if (!userEmail) {
    navigate('/', true);
  }
  const {
    value: email,
    bind: bindEmail,
    reset: resetEmail,
  } = useInput(userEmail || '', 'email', undefined, validateField);
  const {
    value: phoneNumber,
    bind: bindPhoneNumber,
    reset: resetPhoneNumber,
  } = useInput('', 'phoneNumber', undefined, validateField);
  const {
    value: birthday,
    bind: bindBirthday,
    reset: resetBirthday,
  } = useBirthdayInput('', 'birthday', undefined, validateField);
  const { value: birthDate, bind: bindBirthDate } = useSelect('', 'birthDate');
  const { value: birthMonth, bind: bindBirthMonth } = useSelect('', 'birthMonth');
  const { value: birthYear, bind: bindBirthYear } = useSelect('', 'birthYear');

  const {
    value: firstName,
    bind: bindFirstName,
    reset: resetFirstName,
  } = useInput('', 'firstName', undefined, validateField);
  const {
    value: lastName,
    bind: bindLastName,
    reset: resetLastName,
  } = useInput('', 'lastName', undefined, validateField);
  const { value: policy, bind: bindPolicy, reset: resetPolicy } = useCheckbox(false, 'policy');
  const {
    value: canadianOptIn,
    bind: bindCanadianOptIn,
    reset: resetCanadianOptIn,
  } = useCheckbox(false, 'canadianOptIn');
  const { value: address2, bind: bindAddress2, reset: resetAddress2 } = useInput('', 'address2');
  const { value: country, bind: bindCountry, reset: resetCountry } = useSelect('US', 'country');
  const {
    value: address,
    bind: bindAddress,
    reset: resetAddress,
    setValue: setAddressValue,
  } = useInput('', 'address', onInputChange, validateField);
  const { value: state, bind: bindState, reset: resetState } = useSelect('', 'state', undefined, validateField);
  const { value: city, bind: bindCity, reset: resetCity } = useInput('', 'city', undefined, validateField);
  const { value: customState, bind: bindCustomState, reset: resetCustomState } = useInput('', 'customState');
  const { value: zip, bind: bindZip, reset: resetZip } = useInput('', 'zip');

  const {
    value: password,
    bind: bindPassword,
    reset: resetPassword,
    isValid: isValidPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'password', validateField, validateField);

  const [fullAddress, setFullAddress] = useState<AddressObject | undefined>();

  const { params } = usePersistentParameters();
  const { authClient } = useAuthContext();
  const isUSorCA = ['US', 'CA'].includes(country as string);
  const source = useDerivedPropertySlugAsSource();
  const mobile = useMobile();
  const enableIdxApi = useIdxApiEnabled();

  const getCachedPlaceDetails = useCallback(
    async (placeId: string) => {
      if (placeDetailsCache.current?.placeId === placeId) {
        return placeDetailsCache.current.result;
      }
      // Deduplicate concurrent calls for the same placeId — both getFullAddress and getError
      // can fire simultaneously after selection; without this they both make a real API call
      if (placeDetailsInflight.current?.placeId === placeId) {
        return placeDetailsInflight.current.promise;
      }
      const promise = getPlaceDetails(placeId).then(result => {
        placeDetailsCache.current = { placeId, result };
        placeDetailsInflight.current = null;
        refreshGoogleSessionToken();
        return result;
      });
      placeDetailsInflight.current = { placeId, promise };
      return promise;
    },
    [getPlaceDetails, refreshGoogleSessionToken],
  );

  const getFullAddress = useCallback(async () => {
    if (selectedPlaceId) {
      // Skip if cache already has this place's address — avoids repeated API calls and state updates
      if (placeDetailsCache.current?.placeId === selectedPlaceId) {
        return;
      }
      const placeDetails = await getCachedPlaceDetails(selectedPlaceId);
      if (placeDetails.address?.country && placeDetails.address?.city && placeDetails.address?.street) {
        setFullAddress(placeDetails.address);
        return;
      }
    }
    return setFullAddress({
      country: country as string,
      postalCode: zip,
      street: address,
      street2: address2,
      province: isUSorCA ? (state as string) : customState,
      city: city,
    });
  }, [address, address2, city, country, customState, getCachedPlaceDetails, isUSorCA, selectedPlaceId, state, zip]);

  useEffect(() => {
    async function setCanadian() {
      let canadian = false;
      if (email.endsWith('.ca')) {
        canadian = true;
      }

      if (!canadian && (country as string)?.toLowerCase() === 'ca') {
        canadian = true;
      }

      if (!canadian && selectedPlaceId) {
        const place = await getCachedPlaceDetails(selectedPlaceId);
        canadian = place?.address?.country?.toLowerCase() === 'ca';
      }

      setIsCanadian(canadian);
    }

    setCanadian();
  }, [setIsCanadian, email, selectedPlaceId, getCachedPlaceDetails, country]);

  /**
   * Sign the user in after successful sign-up using the Okta IDX in-app pipeline.
   *
   * Migrated from the legacy authn API (`authClient.signIn`) to the IDX API to align with the
   * primary Sign In flow. The IDX pipeline (startAuthenticate → Identify → AuthPassword) lets
   * Okta policy decide whether an additional factor is required at runtime and, on success,
   * exchanges the interaction code for tokens via the Success step handler.
   *
   * The migration is gated by the `OKTA_IDX_API` feature flag (via `useIdxApiEnabled`) and the
   * per-client `isIdxApiEnabled` feature. When either is off we fall back to `authClient.signIn`
   * so toggling the flag OFF instantly reverts to the legacy flow.
   *
   * @returns `true` on success (redirect/navigation dispatched), `false` on unrecoverable error.
   */
  const processSignInWithIdx = useProcessSignInWithIdx('signup');

  const processSignIn = useCallback(
    async (username, password) => {
      try {
        setLoadingState(true);

        // Use IDX in-app pipeline when the OKTA_IDX_API flag is on for this client.
        // When the flag is off we fall back to `authClient.signIn` (authn API) so toggling
        // the flag OFF instantly reverts to the legacy flow (parity with SignIn/index.tsx).
        if (enableIdxApi) {
          const success = await processSignInWithIdx(username, password);
          if (success) {
            return;
          }
          // IDX flow did not succeed — fall through to the legacy authn API so the user
          // still lands somewhere sensible (parity with pre-migration behavior).
        }

        const authResponse: MGMAuthError | void = await authClient.signIn({
          username,
          password,
        });

        if (authResponse instanceof MGMAuthError) {
          redirectToSignIn();
        } else if (mfaEnabled) {
          if (!mgmId) {
            // Extract and set mgmid from idx
            const accessToken = await authClient.getAccessToken();

            if (accessToken?.claims?.uid) {
              setUserMgmId(String(accessToken.claims.uid));
            }
          }
          return navigate(IDENTITY_WIDGET_PATHS.MfaEnroll, true, { [IDW_FLOW_PARAM]: 'signup' });
        } else {
          redirectToApp();
        }
      } catch (err) {
        redirectToSignIn();
      } finally {
        setLoadingState(false);
      }
    },
    [authClient, mfaEnabled, redirectToApp, mgmId, setUserMgmId, enableIdxApi, processSignInWithIdx],
  );

  const processSignUp = useCallback(async () => {
    setLoadingState(true);

    const partnerData =
      params.partner && params.tokenKey
        ? { name: params.partner as string, tokenKey: params.tokenKey as string }
        : undefined;

    const requestBody: IEnrollmentPayload = {
      firstName,
      lastName,
      email,
      login: email,
      mobilePhone: safePhoneNumber(phoneNumber),
      birthDate: dayjs(birthday).format('YYYY-MM-DD') || `${birthYear}-${birthMonth}-${birthDate}`,
      credentials: { password },
      canadianOptIn: isCanadian ? canadianOptIn : undefined,
      address: fullAddress,
      partner: partnerData,
    };

    try {
      if (enrollmentError) {
        setEnrollmentErrorState(null);
      }

      captureEvent('Sign Up', {
        firstName,
        lastName,
        email,
        partner: String(params.partner),
      });
      const { verificationKid } = await enrollUser(
        JSON.stringify(requestBody),
        new Headers({ 'x-mgm-source': source, 'x-mgm-channel': mobile ? 'mobileweb' : 'web' }),
      );

      trackEvent(
        'identity – finish sign up form',
        'finish - sign up form',
        'finishsignup',
        {
          registerState: 'success',
          birthYear: birthday !== '' ? new Date(birthday).getFullYear().toString() : (birthYear as string),
        },
        true,
      );

      setVerificationKid(verificationKid);

      captureMessage('user sign up completed', 'sign-up', { username: requestBody.email });
    } catch (error) {
      let message;
      if (error instanceof KnownError) {
        message = error.message;
      } else {
        message = UNKNOWN_ERROR_MESSAGE;
      }
      setEnrollmentErrorState(message);

      trackEvent('identity – m life - registration submit error', 'm life registration submit error', 'mliferegfail', {
        registerState: 'failed',
        errorDisplayed: message,
      });

      captureException(error as Error, 'sign-up');
    } finally {
      setLoadingState(false);
    }
  }, [
    params.partner,
    params.tokenKey,
    firstName,
    lastName,
    email,
    phoneNumber,
    birthday,
    birthYear,
    birthMonth,
    birthDate,
    password,
    isCanadian,
    canadianOptIn,
    fullAddress,
    enrollmentError,
    source,
    mobile,
    trackEvent,
  ]);

  const verifySignupCode = useCallback(
    async (verificationCode: string) => {
      setLoadingState(true);

      const requestBody = {
        login: email,
        verificationCode,
        verificationKid,
      };

      try {
        if (signUpCodeVerificationError) {
          setSignUpCodeVerificationError(null);
        }

        const response = await signUpVerificationRequest(
          JSON.stringify(requestBody),
          new Headers({ 'x-mgm-source': source, 'x-mgm-channel': mobile ? 'mobileweb' : 'web' }),
        );
        setUserMgmId(response?.mgmId ?? mgmId);

        trackEvent(
          'identity – email verification submit success',
          'email verification success',
          'emailverificationsuccess',
          {
            verifyState: 'success',
          },
        );

        !mfaEnabled && setShowWelcomeMgmRewardsScreen(true);

        processSignIn(email, password);
      } catch (error) {
        let message;
        if (error instanceof KnownError) {
          message = error.message;
        } else {
          message = UNKNOWN_ERROR_MESSAGE;
        }
        setSignUpCodeVerificationError(message);

        trackEvent(
          'identity – email verification submit failure',
          'email verification failure',
          'emailverificationfailure',
          {
            verifyState: 'failure',
            errorDisplayed: message,
          },
        );
        setLoadingState(false);
      }
    },
    [
      email,
      verificationKid,
      signUpCodeVerificationError,
      source,
      mobile,
      trackEvent,
      processSignIn,
      password,
      mfaEnabled,
      setShowWelcomeMgmRewardsScreen,
      setUserMgmId,
      mgmId,
    ],
  );

  const backToSignUp = useCallback(e => {
    e.preventDefault();
    setSignUpCodeVerificationError(null);
    setVerificationKid('');
  }, []);

  const canProcessSignUp = useMemo(() => {
    const isManualCountryUSorCA = ['US', 'CA'].includes(fullAddress?.country as string);
    const isManualAddressValid =
      useManualEntry &&
      fullAddress?.city &&
      fullAddress?.country &&
      fullAddress?.street &&
      (!isManualCountryUSorCA || fullAddress?.province) &&
      (fullAddress?.street?.length ?? 0) <= MaxLengths.Address &&
      isValidZip(fullAddress?.country, fullAddress.postalCode);

    const isAutocompleteAddressValid = !useManualEntry && !!selectedPlaceId;

    return !!(
      email &&
      password &&
      firstName &&
      lastName &&
      policy &&
      isValidPassword &&
      isValidPhoneNumber(phoneNumber) &&
      (isManualAddressValid || isAutocompleteAddressValid) &&
      (birthday || isValidAge(`${birthMonth}/${birthDate}/${birthYear}`)) &&
      !invalidFields.length
    );
  }, [
    birthday,
    email,
    phoneNumber,
    firstName,
    fullAddress,
    invalidFields.length,
    isValidPassword,
    lastName,
    password,
    policy,
    useManualEntry,
    selectedPlaceId,
    birthDate,
    birthMonth,
    birthYear,
  ]);

  // TODO: Replace with A/B test logic
  // For initial roll out we are not going with the three drop downs.
  useEffect(() => {
    setShowExtendedBirthday(false); //(Math.random() < 0.5);
  }, []);

  useEffect(() => {
    if (window?.google && !autocompleteSessionToken) {
      setAutocompleteSessionToken(new google.maps.places.AutocompleteSessionToken());
    }
    // TODO: Look into a way to detecting that google is present without getting into the exhaustive deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window?.google, autocompleteSessionToken]);

  useEffect(() => {
    if (selectedPlaceId) {
      placeDetailsCache.current = null;
      placeDetailsInflight.current = null;
    }
  }, [selectedPlaceId]);

  useEffect(() => {
    getFullAddress();
  }, [getFullAddress]);

  return (
    <SignUpFormContext.Provider
      value={{
        email,
        bindEmail,
        resetEmail,
        firstName,
        bindFirstName,
        resetFirstName,
        lastName,
        bindLastName,
        resetLastName,
        policy,
        bindPolicy,
        resetPolicy,
        password,
        bindPassword,
        resetPassword,
        isValidPassword,
        passwordValidations,
        invalidFields,
        isLoading,
        processSignUp,
        birthday,
        bindBirthday,
        birthDate,
        bindBirthDate,
        birthMonth,
        bindBirthMonth,
        birthYear,
        bindBirthYear,
        bindCustomState,
        resetBirthday,
        phoneNumber,
        bindPhoneNumber,
        resetPhoneNumber,
        address,
        address2,
        city,
        zip,
        state,
        customState,
        resetCustomState,
        country,
        bindAddress,
        resetAddress,
        bindAddress2,
        resetAddress2,
        bindCity,
        resetCity,
        bindZip,
        resetZip,
        bindState,
        resetState,
        bindCountry,
        resetCountry,
        canProcessSignUp,
        placePredictions,
        setSelectedPlaceId,
        selectedPlaceId,
        setAddressValue,
        showExtendedBirthday,
        enrollmentError,
        bindCanadianOptIn,
        resetCanadianOptIn,
        isCanadian,
        autocompleteSessionToken,
        removeInvalidField,
        setInvalidField,
        showWelcomeMgmRewardsScreen,
        verificationKid,
        verifySignupCode,
        signUpCodeVerificationError,
        backToSignUp,
        onInputChange,
        useManualEntry,
        setUseManualEntry,
        getCachedPlaceDetails,
      }}
    >
      {children}
    </SignUpFormContext.Provider>
  );
};
